import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, appId } from '../../firebase-config';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { CalendarIcon, ChartBarIcon, ClockIcon, ExclamationTriangleIcon, PencilIcon, ScaleIcon } from '../Icons';

const PrazoDashboard = ({ onEditPrazo, onOpenCalculator }) => {
  const userId = auth.currentUser?.uid;

  // Estados
  const [prazos, setPrazos] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroUrgencia, setFiltroUrgencia] = useState('todos');

  // Carregar prazos do histórico
  useEffect(() => {
    if (!userId) return;

    const prazosRef = collection(db, `artifacts/${appId}/users/${userId}/prazos_historico`);
    const prazosQuery = query(prazosRef, orderBy('dataCalculo', 'desc'), limit(50));

    const unsubscribe = onSnapshot(prazosQuery, (snapshot) => {
      const prazosData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dataFim: doc.data().dataFim?.toDate ? doc.data().dataFim.toDate() : new Date(doc.data().dataFim),
        dataInicio: doc.data().dataInicio?.toDate ? doc.data().dataInicio.toDate() : new Date(doc.data().dataInicio)
      }));
      setPrazos(prazosData);
    });

    return () => unsubscribe();
  }, [userId, appId]);

  // Carregar tarefas com prazos
  useEffect(() => {
    if (!userId) return;

    const tasksQuery = query(
      collection(db, `artifacts/${appId}/users/${userId}/tasks`),
      where('categoria', '==', 'prazo_juridico')
    );

    const unsubscribe = onSnapshot(tasksQuery, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dueDate: doc.data().dueDate ? new Date(doc.data().dueDate) : null
      }));
      setTasks(tasksData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId, appId]);

  // Calcular métricas dos prazos
  const metricas = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const amanha = new Date(hoje);
    amanha.setDate(amanha.getDate() + 1);

    const proximaSemana = new Date(hoje);
    proximaSemana.setDate(proximaSemana.getDate() + 7);

    const prazosTasks = [...prazos, ...tasks.filter(t => t.dueDate)];

    const vencendoHoje = prazosTasks.filter(p => {
      const dataFim = p.dataFim || p.dueDate;
      return dataFim && 
             new Date(dataFim).toDateString() === hoje.toDateString() &&
             (!p.completed);
    });

    const vencendoAmanha = prazosTasks.filter(p => {
      const dataFim = p.dataFim || p.dueDate;
      return dataFim && 
             new Date(dataFim).toDateString() === amanha.toDateString() &&
             (!p.completed);
    });

    const vencendoSemana = prazosTasks.filter(p => {
      const dataFim = p.dataFim || p.dueDate;
      return dataFim && 
             new Date(dataFim) >= hoje && 
             new Date(dataFim) <= proximaSemana &&
             (!p.completed);
    });

    const vencidos = prazosTasks.filter(p => {
      const dataFim = p.dataFim || p.dueDate;
      return dataFim && 
             new Date(dataFim) < hoje &&
             (!p.completed);
    });

    const urgentes = prazosTasks.filter(p => 
      (p.urgente || p.priority === 'high') && (!p.completed)
    );

    return {
      vencendoHoje: vencendoHoje.length,
      vencendoAmanha: vencendoAmanha.length,
      vencendoSemana: vencendoSemana.length,
      vencidos: vencidos.length,
      urgentes: urgentes.length,
      total: prazosTasks.filter(p => !p.completed).length,
      detalhes: {
        vencendoHoje,
        vencendoAmanha,
        vencendoSemana,
        vencidos,
        urgentes
      }
    };
  }, [prazos, tasks]);

  // Filtrar prazos por urgência
  const prazosParaExibir = useMemo(() => {
    const todosPrazos = [...prazos, ...tasks.filter(t => t.dueDate && !t.completed)];

    let filtered;
    switch (filtroUrgencia) {
      case 'hoje':
        filtered = metricas.detalhes.vencendoHoje;
        break;
      case 'amanha':
        filtered = metricas.detalhes.vencendoAmanha;
        break;
      case 'semana':
        filtered = metricas.detalhes.vencendoSemana;
        break;
      case 'vencidos':
        filtered = metricas.detalhes.vencidos;
        break;
      case 'urgentes':
        filtered = metricas.detalhes.urgentes;
        break;
      default:
        filtered = todosPrazos;
    }
    return filtered.sort((a, b) => {
      const dataA = new Date(a.dataFim || a.dueDate);
      const dataB = new Date(b.dataFim || b.dueDate);
      return dataA - dataB;
    });

  }, [prazos, tasks, metricas, filtroUrgencia]);

  // Função para obter cor do prazo baseada na urgência
  const getCorPrazo = (prazo) => {
    const hoje = new Date();
    hoje.setHours(0,0,0,0);
    const dataFim = new Date(prazo.dataFim || prazo.dueDate);
    dataFim.setHours(0,0,0,0);

    if (!dataFim.valueOf()) return 'gray';

    const diffTime = dataFim - hoje;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'red'; // Vencido
    if (diffDays === 0) return 'red'; // Vence hoje
    if (diffDays === 1) return 'orange'; // Vence amanhã
    if (diffDays <= 7) return 'yellow'; // Vence na semana
    if (prazo.urgente || prazo.priority === 'high') return 'purple'; // Urgente
    return 'blue'; // Normal
  };

  // Função para obter texto de urgência
  const getTextoUrgencia = (prazo) => {
    const hoje = new Date();
    hoje.setHours(0,0,0,0);
    const dataFim = new Date(prazo.dataFim || prazo.dueDate);
    dataFim.setHours(0,0,0,0);

    if (!dataFim.valueOf()) return 'Sem data';

    const diffTime = dataFim - hoje;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return `Venceu há ${Math.abs(diffDays)} dias`;
    if (diffDays === 0) return 'Vence hoje';
    if (diffDays === 1) return 'Vence amanhã';
    if (diffDays <= 7) return `Vence em ${diffDays} dias`;
    return `${diffDays} dias restantes`;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ScaleIcon className="h-8 w-8 text-blue-600" />
            Dashboard de Prazos Jurídicos
          </h1>
          <p className="text-gray-600 mt-1">
            Monitore todos os seus prazos processuais em tempo real
          </p>
        </div>

        <button
          onClick={onOpenCalculator}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <ClockIcon className="h-4 w-4" />
          Calcular Prazo
        </button>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">

        {/* Vencendo Hoje */}
        <div 
          className={`p-4 rounded-lg border-l-4 cursor-pointer transition-colors ${
            filtroUrgencia === 'hoje' ? 'bg-red-50 border-red-500' : 'bg-white border-red-400 hover:bg-red-50'
          }`}
          onClick={() => setFiltroUrgencia(filtroUrgencia === 'hoje' ? 'todos' : 'hoje')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Vencem Hoje</p>
              <p className="text-2xl font-bold text-red-700">{metricas.vencendoHoje}</p>
            </div>
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
          </div>
        </div>

        {/* Vencendo Amanhã */}
        <div 
          className={`p-4 rounded-lg border-l-4 cursor-pointer transition-colors ${
            filtroUrgencia === 'amanha' ? 'bg-orange-50 border-orange-500' : 'bg-white border-orange-400 hover:bg-orange-50'
          }`}
          onClick={() => setFiltroUrgencia(filtroUrgencia === 'amanha' ? 'todos' : 'amanha')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">Vencem Amanhã</p>
              <p className="text-2xl font-bold text-orange-700">{metricas.vencendoAmanha}</p>
            </div>
            <ClockIcon className="h-8 w-8 text-orange-500" />
          </div>
        </div>

        {/* Vencendo na Semana */}
        <div 
          className={`p-4 rounded-lg border-l-4 cursor-pointer transition-colors ${
            filtroUrgencia === 'semana' ? 'bg-yellow-50 border-yellow-500' : 'bg-white border-yellow-400 hover:bg-yellow-50'
          }`}
          onClick={() => setFiltroUrgencia(filtroUrgencia === 'semana' ? 'todos' : 'semana')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 font-medium">Próximos 7 Dias</p>
              <p className="text-2xl font-bold text-yellow-700">{metricas.vencendoSemana}</p>
            </div>
            <CalendarIcon className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        {/* Vencidos */}
        <div 
          className={`p-4 rounded-lg border-l-4 cursor-pointer transition-colors ${
            filtroUrgencia === 'vencidos' ? 'bg-red-50 border-red-600' : 'bg-white border-red-500 hover:bg-red-50'
          }`}
          onClick={() => setFiltroUrgencia(filtroUrgencia === 'vencidos' ? 'todos' : 'vencidos')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700 font-medium">Vencidos</p>
              <p className="text-2xl font-bold text-red-800">{metricas.vencidos}</p>
            </div>
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
          </div>
        </div>

        {/* Total */}
        <div 
          className={`p-4 rounded-lg border-l-4 cursor-pointer transition-colors ${
            filtroUrgencia === 'todos' ? 'bg-blue-50 border-blue-500' : 'bg-white border-blue-400 hover:bg-blue-50'
          }`}
          onClick={() => setFiltroUrgencia('todos')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Ativos</p>
              <p className="text-2xl font-bold text-blue-700">{metricas.total}</p>
            </div>
            <ChartBarIcon className="h-8 w-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Alertas Importantes */}
      {(metricas.vencendoHoje > 0 || metricas.vencidos > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
            <h3 className="text-red-800 font-semibold">Atenção Urgente!</h3>
          </div>
          <div className="text-red-700 text-sm space-y-1">
            {metricas.vencidos > 0 && (
              <p>• {metricas.vencidos} prazo(s) já vencido(s) - Ação imediata necessária!</p>
            )}
            {metricas.vencendoHoje > 0 && (
              <p>• {metricas.vencendoHoje} prazo(s) vencem hoje - Verificar urgentemente!</p>
            )}
          </div>
        </div>
      )}

      {/* Lista de Prazos */}
      <div className="bg-white rounded-lg shadow border">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">
              Prazos {filtroUrgencia !== 'todos' && `- ${
                filtroUrgencia === 'hoje' ? 'Vencendo Hoje' :
                filtroUrgencia === 'amanha' ? 'Vencendo Amanhã' :
                filtroUrgencia === 'semana' ? 'Próximos 7 Dias' :
                filtroUrgencia === 'vencidos' ? 'Vencidos' :
                filtroUrgencia === 'urgentes' ? 'Urgentes' : ''
              }`}
            </h2>

            {filtroUrgencia !== 'todos' && (
              <button
                onClick={() => setFiltroUrgencia('todos')}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Ver todos
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          {prazosParaExibir.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p>Nenhum prazo encontrado</p>
              {filtroUrgencia === 'todos' && (
                <p className="text-sm mt-2">
                  Clique em "Calcular Prazo" para adicionar um novo prazo
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {prazosParaExibir.slice(0, 20).map((prazo) => {
                const cor = getCorPrazo(prazo);
                const urgencia = getTextoUrgencia(prazo);
                const dataFim = new Date(prazo.dataFim || prazo.dueDate);

                return (
                  <div
                    key={prazo.id}
                    className={`p-4 rounded-lg border-l-4 ${
                      cor === 'red' ? 'bg-red-50 border-red-500' :
                      cor === 'orange' ? 'bg-orange-50 border-orange-500' :
                      cor === 'yellow' ? 'bg-yellow-50 border-yellow-500' :
                      cor === 'purple' ? 'bg-purple-50 border-purple-500' :
                      'bg-blue-50 border-blue-500'
                    } hover:shadow-md transition-shadow`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="font-medium text-gray-800">
                          {prazo.descricao || prazo.title || 'Prazo jurídico'}
                        </div>

                        <div className="text-sm text-gray-600 mt-1">
                          {prazo.diasOriginais && (
                            <span>
                              Prazo: {prazo.diasOriginais} dias{' '}
                              {prazo.modalidade === 'dobro' && '(em dobro)'}{' '}
                              • {prazo.tipoContagem?.replace(/_/g, ' ') || 'Prazo processual'}
                            </span>
                          )}
                          {prazo.description && !prazo.diasOriginais && (
                            <span>{prazo.description}</span>
                          )}
                        </div>

                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          {prazo.dataInicio && (
                            <span>
                              Início: {new Date(prazo.dataInicio).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                          <span>
                            Vencimento: {dataFim.toLocaleDateString('pt-BR')} ({dataFim.toLocaleDateString('pt-BR', { weekday: 'short' })})
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col items-end">
                        <div className={`text-sm font-medium ${
                          cor === 'red' ? 'text-red-700' :
                          cor === 'orange' ? 'text-orange-700' :
                          cor === 'yellow' ? 'text-yellow-700' :
                          cor === 'purple' ? 'text-purple-700' :
                          'text-blue-700'
                        }`}>
                          {urgencia}
                        </div>

                        {(prazo.urgente || prazo.priority === 'high') && (
                          <div className="flex items-center gap-1 text-xs text-red-600 mt-1">
                            <ExclamationTriangleIcon className="h-3 w-3" />
                            Urgente
                          </div>
                        )}
                         <button
                            onClick={() => onEditPrazo(prazo)}
                            className="mt-2 p-1 text-gray-500 hover:text-blue-600"
                            title="Editar Prazo"
                         >
                            <PencilIcon className="h-4 w-4" />
                         </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {prazosParaExibir.length > 20 && (
                <div className="text-center py-4 text-gray-500">
                  <p className="text-sm">
                    Mostrando 20 de {prazosParaExibir.length} prazos
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrazoDashboard;